import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Ruler,
  DollarSign,
  User as UserIcon,
  Calendar,
  CheckCircle2,
  Circle,
  Loader2,
  Save,
  FileText,
  ClipboardList,
  StickyNote,
  Activity,
  HardHat,
  Upload,
  Download,
  Trash2,
  MessageCircle,
  Image as ImageIcon,
  Send,
  FileImage,
  FileArchive,
  ScrollText,
  Palette,
  Wrench,
  Map as MapIcon,
  Camera,
  Folder,
  X,
  Eye,
  Share2,
  CheckSquare,
  Square,
  Archive,
  CheckCheck,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  Plus,
  Edit2,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Calendar as CalendarPicker } from "../../components/ui/calendar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "../../components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { api } from "../../lib/api";
import { constructionApi } from "../../lib/copApi";
import { useAuthStore } from "../../store/authStore";
import MentionTextarea, { MentionRichText } from "../../components/construction/MentionTextarea";
import {
  FILE_CATEGORIES,
  CATEGORY_META,
  bucketOf,
  humanFileSize,
} from "../../lib/constructionFileMeta";
import ShareLinkDialog from "../../components/construction/ShareLinkDialog";
import { nameInitials } from "../../lib/nameInitials";
import { contactsApi } from "../../lib/contactsApi";
import PlannedActivitiesTab from "../../components/construction/PlannedActivitiesTab";

// Category taxonomy shared with the cross-project browser lives in
// `lib/constructionFileMeta.js` — imported at the top of this file.

const STATUS_META = {
  green:  { color: "#059669", label: "Aktif" },
  yellow: { color: "#f59e0b", label: "Beklemede" },
  red:    { color: "#dc2626", label: "Sorunlu" },
  gray:   { color: "#9ca3af", label: "Belirsiz" },
};

const fmtMoney = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 }).format(v);
};

// Deadline → { label, tone } for the small badge next to a milestone.
// Compares against **today** in the local timezone (users think in local
// dates when picking one). Overdue is red, ≤3 days is amber, else zinc.
// Completed milestones show a neutral "bitiş" chip.
function computeDeadlineBadge(dateStr, completed) {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const diffMs = target.getTime() - today.getTime();
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const humanDate = target.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "short",
  });
  if (completed) {
    return { label: humanDate, tone: "done", days };
  }
  if (days < 0) {
    const abs = Math.abs(days);
    return { label: `${abs} gün gecikti`, tone: "overdue", days, dateLabel: humanDate };
  }
  if (days === 0) {
    return { label: "Bugün", tone: "urgent", days, dateLabel: humanDate };
  }
  if (days <= 3) {
    return { label: `${days} gün kaldı`, tone: "warn", days, dateLabel: humanDate };
  }
  return { label: `${days} gün kaldı`, tone: "normal", days, dateLabel: humanDate };
}

const DEADLINE_TONE_CLASSES = {
  overdue: "bg-rose-100 text-rose-800 ring-1 ring-rose-200",
  urgent:  "bg-amber-100 text-amber-900 ring-1 ring-amber-200",
  warn:    "bg-amber-50 text-amber-800 ring-1 ring-amber-100",
  normal:  "bg-zinc-100 text-zinc-700 ring-1 ring-zinc-200",
  done:    "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
};

// Grouped milestone template — must stay in sync with
// `DEFAULT_MILESTONE_TEMPLATE` in `backend/routes/construction.py`.
// Progress bar counts completed sub-items against the total, so every
// check bumps progress proportionally (~4% per item).
const DEFAULT_MILESTONES = [
  // 1. Hazırlık
  { key: "prep_material_guide",       label: "Materyal Kılavuzu gönderildi",   group: "preparation",  group_label: "Hazırlık",   completed: false, completed_at: null },
  { key: "prep_exhibitor_guideline",  label: "Exhibitor guideline gönderildi", group: "preparation",  group_label: "Hazırlık",   completed: false, completed_at: null },
  // 2. Tasarım (moved above Sözleşme)
  { key: "design_requested",          label: "Tasarım istendi",                group: "design",       group_label: "Tasarım",    completed: false, completed_at: null },
  { key: "design_reviewed",           label: "Tasarım incelemesi yapıldı",     group: "design",       group_label: "Tasarım",    completed: false, completed_at: null },
  { key: "design_revision_requested", label: "Tasarım revize talep edildi",    group: "design",       group_label: "Tasarım",    completed: false, completed_at: null },
  { key: "design_approved",           label: "Tasarım onaylandı",              group: "design",       group_label: "Tasarım",    completed: false, completed_at: null },
  // 3. Teklif
  { key: "quote_preparing",           label: "Teklif hazırlanıyor",            group: "quote",        group_label: "Teklif",     completed: false, completed_at: null },
  { key: "quote_sent",                label: "Teklif gönderildi",              group: "quote",        group_label: "Teklif",     completed: false, completed_at: null },
  { key: "quote_approved",            label: "Teklif onaylandı",               group: "quote",        group_label: "Teklif",     completed: false, completed_at: null },
  // 4. Sözleşme
  { key: "contract_prepared",         label: "Sözleşme hazırlandı",            group: "contract",     group_label: "Sözleşme",   completed: false, completed_at: null },
  { key: "contract_sent",             label: "Sözleşme gönderildi",            group: "contract",     group_label: "Sözleşme",   completed: false, completed_at: null },
  { key: "contract_revising",         label: "Sözleşme revize ediliyor",       group: "contract",     group_label: "Sözleşme",   completed: false, completed_at: null },
  { key: "contract_signed",           label: "Sözleşme imzalandı",             group: "contract",     group_label: "Sözleşme",   completed: false, completed_at: null },
  // 5. Üretim
  { key: "production_planned",        label: "Üretim planlandı",               group: "production",   group_label: "Üretim",     completed: false, completed_at: null },
  { key: "production_material_prep",  label: "Malzeme hazırlığı",              group: "production",   group_label: "Üretim",     completed: false, completed_at: null },
  { key: "production_started",        label: "Üretim başladı",                 group: "production",   group_label: "Üretim",     completed: false, completed_at: null },
  { key: "production_qc",             label: "Kalite kontrol",                 group: "production",   group_label: "Üretim",     completed: false, completed_at: null },
  { key: "production_completed",      label: "Üretim tamamlandı",              group: "production",   group_label: "Üretim",     completed: false, completed_at: null },
  // 6. Sevkiyat
  { key: "shipping_preparing",        label: "Sevkiyat hazırlığı",             group: "shipping",     group_label: "Sevkiyat",   completed: false, completed_at: null },
  { key: "shipping_sent",             label: "Sevkiyat gönderildi",            group: "shipping",     group_label: "Sevkiyat",   completed: false, completed_at: null },
  { key: "shipping_arrived",          label: "Varış",                          group: "shipping",     group_label: "Sevkiyat",   completed: false, completed_at: null },
  // 7. Kurulum
  { key: "installation_started",      label: "Kurulum başladı",                group: "installation", group_label: "Kurulum",    completed: false, completed_at: null },
  { key: "installation_completed",    label: "Kurulum tamamlandı",             group: "installation", group_label: "Kurulum",    completed: false, completed_at: null },
  // 8. Teslim
  { key: "handover_final_check",      label: "Son kontrol",                    group: "handover",     group_label: "Teslim",     completed: false, completed_at: null },
  { key: "handover_delivered",        label: "Teslim edildi",                  group: "handover",     group_label: "Teslim",     completed: false, completed_at: null },
];

// Tinted chip per group — helps the eye scan the (long) list quickly.
const GROUP_TINT = {
  preparation:  "bg-sky-50    text-sky-800    border-sky-200",
  design:       "bg-violet-50 text-violet-800 border-violet-200",
  quote:        "bg-indigo-50 text-indigo-800 border-indigo-200",
  contract:     "bg-emerald-50 text-emerald-800 border-emerald-200",
  production:   "bg-amber-50  text-amber-800  border-amber-200",
  shipping:     "bg-cyan-50   text-cyan-800   border-cyan-200",
  installation: "bg-rose-50   text-rose-800   border-rose-200",
  handover:     "bg-teal-50   text-teal-800   border-teal-200",
};

// Big prominent progress bar at the top of the dashboard.
function BigProgressBar({ value }) {
  const colour =
    value >= 80 ? "bg-gradient-to-r from-emerald-400 to-emerald-600"
    : value >= 40 ? "bg-gradient-to-r from-amber-400 to-amber-600"
    : "bg-gradient-to-r from-sky-400 to-sky-600";
  return (
    <div className="w-full">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Genel ilerleme
        </span>
        <span className="text-lg font-bold tabular-nums text-zinc-900">
          {value}%
        </span>
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-zinc-200">
        <div
          className={`h-full ${colour} transition-all duration-700`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, sub, tint = "zinc" }) {
  const tints = {
    zinc:    "bg-white border-zinc-200 text-zinc-900",
    green:   "bg-emerald-50 border-emerald-200 text-emerald-900",
    sky:     "bg-sky-50 border-sky-200 text-sky-900",
    violet:  "bg-violet-50 border-violet-200 text-violet-900",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
  };
  return (
    <div className={`rounded-xl border p-4 ${tints[tint]}`}>
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider opacity-70">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] opacity-60">{sub}</div>}
    </div>
  );
}

export default function CopConstructionTrackingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const isSuper = ["super_admin", "ultra_admin"].includes(currentUser?.role);
  const [row, setRow] = useState(null);
  const [assigned, setAssigned] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [trackingNotes, setTrackingNotes] = useState("");
  const [milestones, setMilestones] = useState([]);
  // Collapsed group keys — start with all groups open (empty Set).
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  // Inline label-edit state for super_admin category management.
  const [editingLabel, setEditingLabel] = useState(null); // { scope: "group"|"item", key, value }
  // Manual % override removed per user request; progress derives purely
  // from milestone completion count now.
  // Mention IDs currently @-tagged inside the tracking-notes textarea.
  // Reset on successful save so re-saving the same note doesn't spam.
  const [noteMentionIds, setNoteMentionIds] = useState([]);
  // Same idea for #milestone tags in comments AND notes.
  const [commentMilestoneKeys, setCommentMilestoneKeys] = useState([]);
  const [noteMilestoneKeys, setNoteMilestoneKeys] = useState([]);

  // Timeline + comments
  const [activities, setActivities] = useState([]);
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [mentionIds, setMentionIds] = useState([]);
  // Timeline filter chip: "all" (default) or "mine" — only entries where I
  // authored the action, was @-mentioned, or a milestone I'm assigned to
  // was #-tagged.
  const [timelineFilter, setTimelineFilter] = useState("all");

  // Files (Object Storage)
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("design");
  const fileInputRef = useRef(null);
  // Vertical rail active category. `"all"` shows every uploaded file.
  const [fileCategoryTab, setFileCategoryTab] = useState("all");
  // Fullscreen preview target: { file, blobUrl } — blobUrl is populated
  // by the async fetch inside FilePreviewModal so the modal can show the
  // asset inline for auth'd users.
  const [previewFile, setPreviewFile] = useState(null);
  // fileId → objectURL cache for auto-loaded image thumbnails. We
  // revoke the URLs on unmount and when the file list refreshes so we
  // don't leak blob memory.
  const [thumbUrls, setThumbUrls] = useState({});
  // Drag-and-drop visual feedback on the upload zone.
  const [isDragOver, setIsDragOver] = useState(false);
  // Multi-select for bulk ZIP download — matches the cross-project
  // browser at `/app/cop/files` (İnşaat Dosyaları) so both surfaces
  // behave identically. `Set` for O(1) toggles.
  const [selectedFileIds, setSelectedFileIds] = useState(() => new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  // Share-link dialog target file (null while closed).
  const [shareFile, setShareFile] = useState(null);

  const loadActivities = useCallback(async () => {
    try {
      const data = await api.get(`/construction/${id}/activities`).then((r) => r.data);
      setActivities(data || []);
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  const loadFiles = useCallback(async () => {
    try {
      const data = await api.get(`/construction/${id}/files`).then((r) => r.data);
      setFiles(data || []);
    } catch (err) {
      console.error(err);
    }
  }, [id]);

  // Auto-fetch blob thumbnails for every image file so the grid shows
  // real previews (backend requires bearer auth, so we can't just use
  // `<img src=...>` pointing at the download endpoint). Cached in
  // `thumbUrls` keyed by fileId; URLs revoked whenever the file set
  // changes so we never leak blob memory.
  useEffect(() => {
    let cancelled = false;
    const images = files.filter((f) => (f.content_type || "").startsWith("image/"));
    (async () => {
      const next = {};
      for (const f of images) {
        try {
          const res = await api.get(
            `/construction-files/${f.id}/download`,
            { responseType: "blob" }
          );
          if (cancelled) return;
          next[f.id] = URL.createObjectURL(res.data);
        } catch {
          // Silently skip — the FileCard falls back to a spinner then
          // icon; a broken thumb shouldn't nuke the whole grid.
        }
      }
      if (!cancelled) {
        setThumbUrls((prev) => {
          for (const url of Object.values(prev)) {
            try { URL.revokeObjectURL(url); } catch { /* noop */ }
          }
          return next;
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [files]);

  // Revoke any lingering blob URLs when the page unmounts.
  useEffect(() => {
    return () => {
      setThumbUrls((prev) => {
        for (const url of Object.values(prev)) {
          try { URL.revokeObjectURL(url); } catch { /* noop */ }
        }
        return {};
      });
    };
  }, []);

  // Fetch the row + its assignee.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await constructionApi.get(id);
        setRow(r);
        setTrackingNotes(r.tracking_notes || "");
        setMilestones(
          r.milestones && r.milestones.length > 0
            ? r.milestones
            : DEFAULT_MILESTONES
        );
        if (r.assigned_to_user_id) {
          const users = await api.get("/users?assignable=true").then((rr) => rr.data);
          setAllUsers(users || []);
          setAssigned(users.find((u) => u.id === r.assigned_to_user_id) || null);
        } else {
          // Still need the roster for @mention autocomplete.
          const users = await api.get("/users?assignable=true").then((rr) => rr.data);
          setAllUsers(users || []);
        }
        // Kick off timeline + files in parallel; failures here are non-fatal.
        loadActivities();
        loadFiles();
        // Contacts of kind=person are used by the "Görüşülen Kişi" picker
        // inside the Görüşme Kaydı dialog; companies feed the "Şirket"
        // filter above it so the user starts by picking a company and
        // sees only that company's contacts.
        contactsApi
          .list({ kind: "person" })
          .then((cs) => setAllContacts(cs || []))
          .catch(() => setAllContacts([]));
        contactsApi
          .list({ kind: "company" })
          .then((cs) => setAllCompanies(cs || []))
          .catch(() => setAllCompanies([]));
      } catch (err) {
        toast.error("Kayıt yüklenemedi");
        navigate("/app/cop/construction-tracking");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate, loadActivities, loadFiles]);

  const progress = useMemo(() => {
    if (milestones.length === 0) return 0;
    const done = milestones.filter((m) => m.completed).length;
    return Math.round((done / milestones.length) * 100);
  }, [milestones]);

  // Aggregate KPIs for the Milestones dashboard strip. Keeps a light
  // list of the top-3 overdue + at-risk milestone labels so users can
  // scan the risks without expanding every group.
  const milestoneStats = useMemo(() => {
    const total = milestones.length;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let completed = 0;
    let overdueCount = 0;
    let atRiskCount = 0;
    let upcomingCount = 0;
    let noDeadlineOpen = 0;
    let mostOverdueDays = 0;
    const overdueList = [];
    const atRiskList = [];
    for (const m of milestones) {
      if (m.completed) {
        completed += 1;
        continue;
      }
      if (!m.deadline) {
        noDeadlineOpen += 1;
        continue;
      }
      const target = new Date(`${m.deadline}T00:00:00`);
      if (Number.isNaN(target.getTime())) {
        noDeadlineOpen += 1;
        continue;
      }
      const days = Math.round((target.getTime() - today.getTime()) / 86400000);
      if (days < 0) {
        overdueCount += 1;
        const overdue = Math.abs(days);
        if (overdue > mostOverdueDays) mostOverdueDays = overdue;
        overdueList.push({ key: m.key, label: m.label, group: m.group_label, days: overdue, deadline: m.deadline });
      } else if (days <= 3) {
        atRiskCount += 1;
        atRiskList.push({ key: m.key, label: m.label, group: m.group_label, days, deadline: m.deadline });
      } else {
        upcomingCount += 1;
      }
    }
    // Sort risk lists so the most urgent surface first.
    overdueList.sort((a, b) => b.days - a.days);
    atRiskList.sort((a, b) => a.days - b.days);
    const openCount = total - completed;
    return {
      total,
      completed,
      completionPct: total > 0 ? Math.round((completed / total) * 100) : 0,
      overdueCount,
      overduePct: openCount > 0 ? Math.round((overdueCount / openCount) * 100) : 0,
      atRiskCount,
      atRiskPct: openCount > 0 ? Math.round((atRiskCount / openCount) * 100) : 0,
      upcomingCount,
      noDeadlineOpen,
      mostOverdueDays,
      overdueList: overdueList.slice(0, 3),
      atRiskList: atRiskList.slice(0, 3),
    };
  }, [milestones]);

  const toggleMilestone = (idx) => {
    setMilestones((prev) =>
      prev.map((m, i) =>
        i === idx
          ? {
              ...m,
              completed: !m.completed,
              completed_at: !m.completed ? new Date().toISOString().slice(0, 10) : null,
            }
          : m
      )
    );
  };

  // Set / clear the deadline (yyyy-mm-dd string) on a single milestone.
  // Passing `null` clears it. Any user can edit; the backend PATCH
  // permits deadline changes without the super_admin gate.
  const setMilestoneDeadline = (idx, dateStr) => {
    setMilestones((prev) =>
      prev.map((m, i) =>
        i === idx
          ? {
              ...m,
              deadline: dateStr || null,
              // Reset the notification stamp so the checker treats the
              // new date as a fresh window.
              deadline_notified_at: null,
            }
          : m
      )
    );
  };

  // Toggle a single user's assignment on a specific milestone. Used by
  // the compact "Sorumlular" chip picker below each milestone row.
  const toggleMilestoneAssignee = (idx, userId) => {
    setMilestones((prev) =>
      prev.map((m, i) => {
        if (i !== idx) return m;
        const set = new Set(m.assigned_user_ids || []);
        if (set.has(userId)) set.delete(userId);
        else set.add(userId);
        return { ...m, assigned_user_ids: Array.from(set) };
      })
    );
  };

  // --- Collapse / expand group headers ------------------------------
  const toggleGroupCollapsed = useCallback((groupKey) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);

  // --- Super_admin: category management ----------------------------
  // Slug helper so users can type "Ölçüm Turu" and we still generate a
  // stable, ascii key ("group_olcum_turu") for the DB.
  const _slug = (s) => {
    return (s || "")
      .toLowerCase()
      .replaceAll("ı", "i")
      .replaceAll("ö", "o")
      .replaceAll("ü", "u")
      .replaceAll("ş", "s")
      .replaceAll("ç", "c")
      .replaceAll("ğ", "g")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || "unnamed";
  };

  const addSubItem = (groupKey, groupLabel) => {
    const label = window.prompt(`"${groupLabel}" grubuna yeni alt kategori adı:`);
    if (!label || !label.trim()) return;
    const slug = _slug(label);
    const existingKeys = new Set(milestones.map((m) => m.key));
    let key = `${groupKey}_${slug}`;
    let n = 1;
    while (existingKeys.has(key)) key = `${groupKey}_${slug}_${++n}`;
    // Insert at the end of the group (last matching index + 1).
    setMilestones((prev) => {
      const lastIdx = prev.map((m, i) => (m.group === groupKey ? i : -1)).filter((i) => i >= 0).pop();
      const insertAt = lastIdx === undefined ? prev.length : lastIdx + 1;
      const next = [...prev];
      next.splice(insertAt, 0, {
        key,
        label: label.trim(),
        group: groupKey,
        group_label: groupLabel,
        completed: false,
        completed_at: null,
        assigned_user_ids: [],
      });
      return next;
    });
  };

  const deleteSubItem = (msKey) => {
    const target = milestones.find((m) => m.key === msKey);
    if (!target) return;
    if (!window.confirm(`"${target.label}" alt kategorisi silinsin mi?`)) return;
    setMilestones((prev) => prev.filter((m) => m.key !== msKey));
  };

  const addGroup = () => {
    const label = window.prompt("Yeni grup adı:");
    if (!label || !label.trim()) return;
    const slug = _slug(label);
    const existingGroups = new Set(milestones.map((m) => m.group));
    let groupKey = `group_${slug}`;
    let n = 1;
    while (existingGroups.has(groupKey)) groupKey = `group_${slug}_${++n}`;
    // Also prompt for the first sub-item so an empty group doesn't
    // clutter the UI (progress denominator would grow with a group that
    // has zero items — useless).
    const firstItem = window.prompt(
      `"${label.trim()}" grubunun ilk alt kategorisi:`,
      label.trim()
    );
    if (!firstItem || !firstItem.trim()) return;
    setMilestones((prev) => [
      ...prev,
      {
        key: `${groupKey}_${_slug(firstItem)}`,
        label: firstItem.trim(),
        group: groupKey,
        group_label: label.trim(),
        completed: false,
        completed_at: null,
        assigned_user_ids: [],
      },
    ]);
  };

  const commitLabelEdit = () => {
    if (!editingLabel || !editingLabel.value.trim()) {
      setEditingLabel(null);
      return;
    }
    const newLabel = editingLabel.value.trim();
    if (editingLabel.scope === "group") {
      // Rename group_label on every milestone in this group.
      setMilestones((prev) =>
        prev.map((m) =>
          m.group === editingLabel.key ? { ...m, group_label: newLabel } : m
        )
      );
    } else {
      setMilestones((prev) =>
        prev.map((m) =>
          m.key === editingLabel.key ? { ...m, label: newLabel } : m
        )
      );
    }
    setEditingLabel(null);
  };

  // Shape passed to the MentionTextarea `tags` prop — includes the
  // current assignee count so users know how many people will be pinged
  // when they pick a milestone.
  const milestoneTagOptions = useMemo(
    () =>
      milestones.map((m) => ({
        key: m.key,
        label: m.label,
        assignees_count: (m.assigned_user_ids || []).length,
      })),
    [milestones]
  );

  // Milestone keys where the current user is an assignee — used by the
  // "Sadece bana yönelik" timeline filter so #-tagged activities land
  // in the filtered view when they hit one of my milestones.
  const myMilestoneKeys = useMemo(() => {
    const uid = currentUser?.id;
    if (!uid) return new Set();
    return new Set(
      milestones
        .filter((m) => (m.assigned_user_ids || []).includes(uid))
        .map((m) => m.key)
    );
  }, [milestones, currentUser?.id]);

  // Client-side filter for the timeline chip. Cheap enough at N=few-hundred
  // that we don't need a backend `mine_only` param.
  const filteredActivities = useMemo(() => {
    if (timelineFilter !== "mine") return activities;
    const uid = currentUser?.id;
    if (!uid) return activities;
    return activities.filter((a) => {
      if (a.actor_user_id === uid) return true;
      const mentions = a.meta?.mentioned_user_ids || [];
      if (mentions.includes(uid)) return true;
      const tags = a.meta?.mentioned_milestone_keys || [];
      if (tags.some((k) => myMilestoneKeys.has(k))) return true;
      return false;
    });
  }, [activities, timelineFilter, currentUser?.id, myMilestoneKeys]);

  const saveTracking = async () => {
    setSaving(true);
    try {
      const previousNotes = row?.tracking_notes || "";
      const payload = {
        milestones,
        tracking_notes: trackingNotes,
        // Manual % override removed — progress is fully derived from the
        // completed milestone count on the backend/frontend both.
        progress_percent: 0,
      };
      const updated = await constructionApi.update(id, payload);
      setRow(updated);

      // Fire mention notifications when (a) the note actually changed
      // and (b) at least one @-tag OR #tag is present. Prevents spamming
      // when the user hits save just to sync milestones.
      const hasNoteMentions =
        (noteMentionIds.length > 0 || noteMilestoneKeys.length > 0) &&
        trackingNotes.trim() !== previousNotes.trim();
      if (hasNoteMentions) {
        try {
          const resp = await api.post(`/construction/${id}/note-mentions`, {
            mentioned_user_ids: noteMentionIds,
            mentioned_milestone_keys: noteMilestoneKeys,
            note_text: trackingNotes,
          });
          if (resp.data?.dispatched > 0) {
            toast.success(
              `Kaydedildi · ${resp.data.dispatched} kişi bilgilendirildi`
            );
          } else {
            toast.success("Takip bilgileri kaydedildi");
          }
        } catch {
          toast.success("Takip bilgileri kaydedildi");
        }
        setNoteMentionIds([]);
        setNoteMilestoneKeys([]);
      } else {
        toast.success("Takip bilgileri kaydedildi");
      }
      // Milestone toggles emit activity entries server-side — refresh the feed.
      loadActivities();
    } catch (err) {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const postComment = async () => {
    const text = comment.trim();
    if (!text) return;
    setPosting(true);
    try {
      await api.post(`/construction/${id}/activities`, {
        text,
        mentioned_user_ids: mentionIds,
        mentioned_milestone_keys: commentMilestoneKeys,
      });
      setComment("");
      setMentionIds([]);
      setCommentMilestoneKeys([]);
      loadActivities();
      const pingedCount = mentionIds.length + commentMilestoneKeys.length;
      toast.success(
        pingedCount > 0
          ? `Yorum eklendi · ${mentionIds.length} kişi + ${commentMilestoneKeys.length} etiket`
          : "Yorum eklendi"
      );
    } catch (err) {
      toast.error("Yorum gönderilemedi");
    } finally {
      setPosting(false);
    }
  };

  const uploadFile = async (fileObj) => {
    if (!fileObj) return;
    if (fileObj.size > 500 * 1024 * 1024) {
      toast.error("Dosya çok büyük (max 500MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      fd.append("category", uploadCategory);
      await api.post(`/construction/${id}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`"${fileObj.name}" yüklendi`);
      loadFiles();
      loadActivities();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Yükleme başarısız";
      toast.error(String(msg));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = async (fileId, name) => {
    if (!window.confirm(`"${name}" silinsin mi?`)) return;
    try {
      await api.delete(`/construction-files/${fileId}`);
      toast.success("Dosya silindi");
      loadFiles();
      loadActivities();
    } catch (err) {
      toast.error("Silme başarısız");
    }
  };

  const downloadFile = async (fileId, name) => {
    try {
      const res = await api.get(`/construction-files/${fileId}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("İndirilemedi");
    }
  };

  // --- Selection helpers (mirrors CopFilesConstruction) --------------
  const toggleFileSelected = useCallback((fileId) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);
  const clearFileSelection = useCallback(
    () => setSelectedFileIds(new Set()),
    []
  );
  const selectAllShownFiles = useCallback((shownList) => {
    setSelectedFileIds((prev) => {
      const shownIds = shownList.map((f) => f.id);
      const allSelected = shownIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) shownIds.forEach((id) => next.delete(id));
      else shownIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const bulkDownloadFiles = useCallback(async () => {
    const ids = Array.from(selectedFileIds);
    if (ids.length === 0) return;
    if (ids.length > 100) {
      toast.error("En fazla 100 dosya seçilebilir");
      return;
    }
    setBulkDownloading(true);
    try {
      const res = await constructionApi.bulkDownloadFiles(ids);
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "application/zip" })
      );
      let filename = "insaat-dosyalar.zip";
      const disp = res.headers?.["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(disp);
      if (m) filename = m[1];
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const count = res.headers?.["x-file-count"] || ids.length;
      toast.success(`${count} dosya ZIP olarak indirildi`);
      clearFileSelection();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Toplu indirme başarısız";
      toast.error(String(msg));
    } finally {
      setBulkDownloading(false);
    }
  }, [selectedFileIds, clearFileSelection]);

  if (loading || !row) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  const status = STATUS_META[row.status] || STATUS_META.gray;
  const perM2 = row.stand_cost && row.size_m2 && Number(row.size_m2) > 0
    ? Math.round(Number(row.stand_cost) / Number(row.size_m2))
    : null;

  return (
    <div
      className="mx-auto max-w-[1400px] p-4 sm:p-6 md:p-8"
      data-testid="tracking-detail-page"
    >
      {/* Breadcrumb / back */}
      <div className="mb-4 flex items-center gap-2 text-sm">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/app/cop/construction-tracking")}
          data-testid="tracking-back-btn"
          className="h-7 gap-1 text-zinc-500 hover:text-zinc-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          İnşaat Takip
        </Button>
        <span className="text-zinc-300">/</span>
        <span className="font-medium text-zinc-900">{row.company_name || "—"}</span>
      </div>

      {/* Dashboard header — big cover-style banner with progress bar on top */}
      <div className="mb-5 overflow-hidden rounded-2xl border border-zinc-200 bg-gradient-to-br from-white to-zinc-50 shadow-sm">
        {/* Progress bar sits at the very top */}
        <div className="px-6 pt-5">
          <BigProgressBar value={progress} />
        </div>

        <div className="px-6 pb-5 pt-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1
                  className="text-2xl font-bold tracking-tight text-zinc-900"
                  data-testid="tracking-detail-title"
                >
                  {row.company_name || "Firma"}
                </h1>
                {row.stand_no && (
                  <span className="rounded-md bg-zinc-900 px-2 py-0.5 text-sm font-semibold text-white">
                    Stant #{row.stand_no}
                  </span>
                )}
                <span
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                  style={{
                    backgroundColor: `${status.color}22`,
                    color: status.color,
                  }}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: status.color }}
                  />
                  {status.label}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-zinc-500">
                {row.hub_name && (
                  <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700">
                    {row.hub_name}
                  </span>
                )}
                {row.country && <span>{row.country}</span>}
                {row.stage && (
                  <>
                    <span className="text-zinc-300">·</span>
                    <span>Aşama: <strong className="text-zinc-700">{row.stage}</strong></span>
                  </>
                )}
              </div>
            </div>

            <Button
              onClick={saveTracking}
              disabled={saving}
              data-testid="tracking-save-btn"
              className="shrink-0"
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Takibi kaydet
            </Button>
          </div>

          {/* KPI tiles below the header — pulled from the construction row */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <KpiTile
              icon={Ruler}
              tint="sky"
              label="Alan"
              value={row.size_m2 ? `${row.size_m2} m²` : "—"}
              sub={row.height_cm ? `H: ${row.height_cm} cm` : null}
            />
            <KpiTile
              icon={DollarSign}
              tint="violet"
              label="Bütçe"
              value={row.stand_cost ? `${fmtMoney(row.stand_cost)} ${row.currency || ""}` : "—"}
              sub={perM2 ? `${fmtMoney(perM2)} ${row.currency}/m²` : null}
            />
            <KpiTile
              icon={UserIcon}
              tint="green"
              label="Sorumlu"
              value={assigned?.name || "—"}
            />
            <KpiTile
              icon={HardHat}
              tint="amber"
              label="Stand Türü"
              value={row.stand_type || "—"}
            />
            <KpiTile
              icon={Building2}
              tint="zinc"
              label="Kontak"
              value={row.contact_name || "—"}
              sub={row.contact_email || null}
            />
            <KpiTile
              icon={CheckCircle2}
              tint="rose"
              label="Kilometre taşları"
              value={`${milestones.filter((m) => m.completed).length}/${milestones.length}`}
              sub="Tamamlanan"
            />
          </div>
        </div>
      </div>

      {/* Tabs section */}
      <Tabs defaultValue="genel" className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto rounded-lg bg-zinc-100 p-1">
          <TabsTrigger value="genel" data-testid="tab-genel">
            <ClipboardList className="mr-1.5 h-3.5 w-3.5" /> Genel
          </TabsTrigger>
          <TabsTrigger value="milestones" data-testid="tab-milestones">
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Kilometre Taşları
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">
            <StickyNote className="mr-1.5 h-3.5 w-3.5" /> Notlar
          </TabsTrigger>
          <TabsTrigger value="planned" data-testid="tab-planned">
            <Calendar className="mr-1.5 h-3.5 w-3.5" /> Aktiviteler
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">
            <Activity className="mr-1.5 h-3.5 w-3.5" /> Zaman Çizelgesi
          </TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">
            <FileText className="mr-1.5 h-3.5 w-3.5" /> Dosyalar
          </TabsTrigger>
        </TabsList>

        {/* === Genel === */}
        <TabsContent value="genel" className="mt-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <section className="rounded-xl border border-zinc-200 bg-white p-5">
              <h3 className="mb-3 text-sm font-semibold text-zinc-500">Temel Bilgiler</h3>
              <dl className="space-y-2 text-sm">
                <Row k="Firma" v={row.company_name} />
                <Row k="Ülke" v={row.country} />
                <Row k="Hub" v={row.hub_name} />
                <Row k="Stant No" v={row.stand_no ? `#${row.stand_no}` : "—"} />
                <Row k="Aşama" v={row.stage} />
              </dl>
            </section>

            <section className="rounded-xl border border-zinc-200 bg-white p-5">
              <h3 className="mb-3 text-sm font-semibold text-zinc-500">Kontak Bilgileri</h3>
              <dl className="space-y-2 text-sm">
                <Row k="Kişi" v={row.contact_name} />
                <Row k="E-posta" v={row.contact_email} />
                <Row k="Telefon" v={row.contact_phone} />
                <Row k="Sorumlu" v={assigned?.name} />
              </dl>
            </section>

            <section className="rounded-xl border border-zinc-200 bg-white p-5">
              <h3 className="mb-3 text-sm font-semibold text-zinc-500">Stand Gereksinimleri</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-amber-50 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-amber-800">
                    Yükseklik
                  </div>
                  <div className="mt-1 text-lg font-semibold text-amber-900">
                    {row.height_cm ? `${row.height_cm} cm` : "—"}
                  </div>
                </div>
                <div className="rounded-lg bg-sky-50 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-sky-800">
                    Alan
                  </div>
                  <div className="mt-1 text-lg font-semibold text-sky-900">
                    {row.size_m2 ? `${row.size_m2} m²` : "—"}
                  </div>
                </div>
                <div className="col-span-2 rounded-lg bg-zinc-50 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-500">
                    Stand Türü
                  </div>
                  <div className="mt-1 text-sm font-medium text-zinc-900">
                    {row.stand_type || "—"}
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-zinc-200 bg-white p-5">
              <h3 className="mb-3 text-sm font-semibold text-zinc-500">Finansal Bilgiler</h3>
              <div className="space-y-3">
                <div className="rounded-lg bg-emerald-50 p-3">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-emerald-800">
                    Toplam Bütçe
                  </div>
                  <div className="mt-1 text-2xl font-bold text-emerald-900">
                    {row.stand_cost ? `${fmtMoney(row.stand_cost)} ${row.currency || ""}` : "—"}
                  </div>
                </div>
                {perM2 && (
                  <div className="rounded-lg bg-violet-50 p-3">
                    <div className="text-[10px] font-medium uppercase tracking-wide text-violet-800">
                      m² Birim Fiyatı
                    </div>
                    <div className="mt-1 text-lg font-semibold text-violet-900">
                      {fmtMoney(perM2)} {row.currency}/m²
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="rounded-xl border border-zinc-200 bg-white p-5 md:col-span-2">
              <h3 className="mb-3 text-sm font-semibold text-zinc-500">Notlar (Genel)</h3>
              <p className="whitespace-pre-wrap text-sm text-zinc-700">
                {row.notes || <span className="italic text-zinc-400">Not girilmemiş.</span>}
              </p>
            </section>
          </div>
        </TabsContent>

        {/* === Milestones === */}
        <TabsContent value="milestones" className="mt-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <h3 className="text-lg font-semibold">Kilometre Taşları</h3>
                <p className="mt-0.5 text-sm text-zinc-500">
                  Her aşamayı işaretle — ilerleme çubuğu otomatik güncellenir.
                </p>
              </div>
              {/* Realization vs. target — replaces the old manual % input.
                  Reads as "current% / 100%" so the team sees how far they
                  are from full completion at a glance. */}
              <div
                className="flex flex-col items-end rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-2"
                data-testid="tracking-progress-readout"
              >
                <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  Gerçekleşme oranı
                </div>
                <div className="mt-0.5 flex items-baseline gap-1">
                  <span
                    className={`text-2xl font-bold tabular-nums ${
                      progress >= 100 ? "text-emerald-600" : progress >= 40 ? "text-amber-600" : "text-sky-600"
                    }`}
                  >
                    %{progress}
                  </span>
                  <span className="text-sm font-medium text-zinc-400">/ %100</span>
                </div>
                <div className="mt-0.5 text-[10px] text-zinc-500">
                  {milestones.filter((m) => m.completed).length} / {milestones.length} tamamlandı
                </div>
              </div>
            </div>

            {/* Bulk-assign toolbar removed. The row-level SORUMLU is now
                auto-assigned to every milestone (server-side) and extra
                collaborators can be added per-item via each milestone's
                "+ Ekle" picker (additive, non-destructive). */}

            {/* ==== Milestones dashboard strip ====
                4 KPI tiles + a compact "kritik" panel that surfaces
                overdue + at-risk milestone labels so the team can jump
                straight to the risks. Recomputed from `milestones` via
                the `milestoneStats` memo. */}
            <div className="mb-5" data-testid="milestones-dashboard">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <KpiTile
                  icon={CheckCircle2}
                  label="Tamamlanan"
                  value={`${milestoneStats.completed} / ${milestoneStats.total}`}
                  sub={`%${milestoneStats.completionPct} tamamlandı`}
                  tint="green"
                />
                <KpiTile
                  icon={AlertTriangle}
                  label="Gecikmiş"
                  value={milestoneStats.overdueCount}
                  sub={
                    milestoneStats.overdueCount > 0
                      ? `En eski: ${milestoneStats.mostOverdueDays} gün · %${milestoneStats.overduePct} açık iş`
                      : "Gecikme yok"
                  }
                  tint={milestoneStats.overdueCount > 0 ? "rose" : "zinc"}
                />
                <KpiTile
                  icon={Clock}
                  label="Riskte"
                  value={milestoneStats.atRiskCount}
                  sub={
                    milestoneStats.atRiskCount > 0
                      ? `≤ 3 gün · %${milestoneStats.atRiskPct} açık iş`
                      : "Yakın deadline yok"
                  }
                  tint={milestoneStats.atRiskCount > 0 ? "amber" : "zinc"}
                />
                <KpiTile
                  icon={TrendingUp}
                  label="Bekleyen"
                  value={milestoneStats.upcomingCount + milestoneStats.noDeadlineOpen}
                  sub={
                    milestoneStats.noDeadlineOpen > 0
                      ? `${milestoneStats.upcomingCount} planlı · ${milestoneStats.noDeadlineOpen} tarihsiz`
                      : `${milestoneStats.upcomingCount} planlı`
                  }
                  tint="sky"
                />
              </div>

              {(milestoneStats.overdueList.length > 0 ||
                milestoneStats.atRiskList.length > 0) && (
                <div
                  className="mt-3 grid gap-3 sm:grid-cols-2"
                  data-testid="milestones-dashboard-critical"
                >
                  {milestoneStats.overdueList.length > 0 && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-3">
                      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-rose-900">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Gecikmiş görevler
                        <span className="ml-auto rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-800">
                          {milestoneStats.overdueCount}
                        </span>
                      </div>
                      <ul className="space-y-1.5">
                        {milestoneStats.overdueList.map((it) => (
                          <li
                            key={it.key}
                            data-testid={`milestone-overdue-${it.key}`}
                            className="flex items-center gap-2 text-[12px]"
                          >
                            <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
                            <span className="min-w-0 flex-1 truncate text-zinc-900">
                              <span className="text-zinc-500">{it.group} · </span>
                              {it.label}
                            </span>
                            <span className="shrink-0 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800">
                              {it.days} gün gecikti
                            </span>
                          </li>
                        ))}
                        {milestoneStats.overdueCount > milestoneStats.overdueList.length && (
                          <li className="pt-1 text-[10px] italic text-rose-700">
                            +{milestoneStats.overdueCount - milestoneStats.overdueList.length} tane daha aşağıda
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                  {milestoneStats.atRiskList.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3">
                      <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-amber-900">
                        <Clock className="h-3.5 w-3.5" />
                        Yakında dolacak (≤ 3 gün)
                        <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">
                          {milestoneStats.atRiskCount}
                        </span>
                      </div>
                      <ul className="space-y-1.5">
                        {milestoneStats.atRiskList.map((it) => (
                          <li
                            key={it.key}
                            data-testid={`milestone-atrisk-${it.key}`}
                            className="flex items-center gap-2 text-[12px]"
                          >
                            <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                            <span className="min-w-0 flex-1 truncate text-zinc-900">
                              <span className="text-zinc-500">{it.group} · </span>
                              {it.label}
                            </span>
                            <span className="shrink-0 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                              {it.days === 0 ? "Bugün" : `${it.days} gün`}
                            </span>
                          </li>
                        ))}
                        {milestoneStats.atRiskCount > milestoneStats.atRiskList.length && (
                          <li className="pt-1 text-[10px] italic text-amber-700">
                            +{milestoneStats.atRiskCount - milestoneStats.atRiskList.length} tane daha aşağıda
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <ol className="space-y-4" data-testid="milestones-list">
              {(() => {
                // Bucket the flat list into ordered groups. `__idx`
                // preserves the milestones-array index so toggles keep
                // pointing at the right row after sub-item add/delete.
                const groups = [];
                let current = null;
                milestones.forEach((m, idx) => {
                  if (!current || current.key !== m.group) {
                    current = {
                      key: m.group || "misc",
                      label: m.group_label || "Diğer",
                      items: [],
                    };
                    groups.push(current);
                  }
                  current.items.push({ ...m, __idx: idx });
                });
                return groups.map((g) => {
                  const done = g.items.filter((it) => it.completed).length;
                  const total = g.items.length;
                  const isFull = total > 0 && done === total;
                  const isCollapsed = collapsedGroups.has(g.key);
                  // Full groups switch to a saturated emerald banner so
                  // the team can spot completed phases at a glance.
                  const tint = isFull
                    ? "bg-emerald-500 text-white border-emerald-600"
                    : (GROUP_TINT[g.key] || "bg-zinc-50 text-zinc-800 border-zinc-200");
                  const isEditingGroup =
                    editingLabel?.scope === "group" && editingLabel.key === g.key;
                  return (
                    <li
                      key={g.key}
                      data-testid={`milestone-group-${g.key}`}
                      data-full={isFull ? "true" : "false"}
                      className={`overflow-hidden rounded-xl border ${
                        isFull ? "border-emerald-500 shadow-sm shadow-emerald-100" : "border-zinc-200"
                      } bg-white`}
                    >
                      {/* Group header — clickable toggle */}
                      <div
                        className={`flex items-center gap-2 border-b px-4 py-2.5 ${tint}`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleGroupCollapsed(g.key)}
                          data-testid={`milestone-group-toggle-${g.key}`}
                          className="flex items-center gap-2 focus:outline-none"
                        >
                          {isCollapsed ? (
                            <ChevronRightIcon className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                          {isEditingGroup ? (
                            <input
                              autoFocus
                              value={editingLabel.value}
                              onChange={(e) =>
                                setEditingLabel({ ...editingLabel, value: e.target.value })
                              }
                              onBlur={commitLabelEdit}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") commitLabelEdit();
                                if (e.key === "Escape") setEditingLabel(null);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`milestone-group-edit-input-${g.key}`}
                              className="rounded-md border border-white/40 bg-white/10 px-2 py-0.5 text-[13px] font-semibold uppercase tracking-wider text-current outline-none placeholder:text-current/70"
                            />
                          ) : (
                            <span className="text-[13px] font-semibold uppercase tracking-wider">
                              {g.label}
                            </span>
                          )}
                        </button>
                        <span
                          className={`ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
                            isFull ? "bg-white/25 text-white" : "bg-white/70 text-inherit"
                          }`}
                        >
                          {isFull && <CheckCircle2 className="h-3 w-3" />}
                          {done}/{total}
                        </span>
                        {isSuper && !isEditingGroup && (
                          <div className="flex items-center gap-0.5 opacity-80 hover:opacity-100">
                            {/* Edit moved to Settings → "Kilometre Taşı
                                Şablonu". Kept a subtle link so super
                                admins know where to go. */}
                          </div>
                        )}
                      </div>

                      {/* Sub-items — hidden when the group is collapsed */}
                      {!isCollapsed && (
                        <div className="divide-y divide-zinc-100">
                          {g.items.map((m) => {
                            const idx = m.__idx;
                            const assignees = (m.assigned_user_ids || [])
                              .map((uid) => allUsers.find((u) => u.id === uid))
                              .filter(Boolean);
                            const isEditingItem =
                              editingLabel?.scope === "item" && editingLabel.key === m.key;
                            return (
                              <div
                                key={m.key}
                                data-testid={`milestone-${m.key}`}
                                className={`flex items-start gap-3 px-4 py-2.5 transition-colors ${
                                  // Darker green bg for completed rows —
                                  // was `bg-emerald-50/40` before, now
                                  // more visible per user request.
                                  m.completed ? "bg-emerald-100/70" : "bg-white"
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() => toggleMilestone(idx)}
                                  className="mt-0.5 shrink-0"
                                  aria-label={`Toggle ${m.label}`}
                                >
                                  {m.completed ? (
                                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                  ) : (
                                    <Circle className="h-5 w-5 text-zinc-300" />
                                  )}
                                </button>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2">
                                    {isEditingItem ? (
                                      <input
                                        autoFocus
                                        value={editingLabel.value}
                                        onChange={(e) =>
                                          setEditingLabel({ ...editingLabel, value: e.target.value })
                                        }
                                        onBlur={commitLabelEdit}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") commitLabelEdit();
                                          if (e.key === "Escape") setEditingLabel(null);
                                        }}
                                        data-testid={`milestone-edit-input-${m.key}`}
                                        className="flex-1 rounded-md border border-zinc-300 px-2 py-1 text-[13px] font-medium outline-none focus:border-violet-400"
                                      />
                                    ) : (
                                      <span
                                        className={`text-[13px] font-medium ${
                                          m.completed ? "text-emerald-900" : "text-zinc-900"
                                        }`}
                                      >
                                        {m.label}
                                      </span>
                                    )}
                                    <div className="flex shrink-0 items-center gap-1.5">
                                      {(() => {
                                        const badge = computeDeadlineBadge(m.deadline, m.completed);
                                        if (!badge) return null;
                                        return (
                                          <span
                                            data-testid={`milestone-deadline-badge-${m.key}`}
                                            title={badge.dateLabel ? `Deadline: ${badge.dateLabel}` : `Deadline: ${badge.label}`}
                                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tabular-nums ${DEADLINE_TONE_CLASSES[badge.tone]}`}
                                          >
                                            <Calendar className="h-3 w-3" />
                                            {badge.label}
                                          </span>
                                        );
                                      })()}
                                      {m.completed && m.completed_at && (
                                        <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700">
                                          <CheckCircle2 className="h-3 w-3" />
                                          {m.completed_at}
                                        </span>
                                      )}
                                      {/* Edit / delete moved to Ayarlar
                                          → "Kilometre Taşı Şablonu" so
                                          template changes reflect on
                                          every project at once. */}
                                    </div>
                                  </div>

                                  {/* Assignees + picker */}
                                  <div className="mt-1 flex items-center gap-2">
                                    <span className="text-[10px] text-zinc-500">Sorumlular:</span>
                                    <div
                                      className="flex items-center -space-x-1"
                                      data-testid={`milestone-assignees-${m.key}`}
                                    >
                                      {assignees.length === 0 && (
                                        <span className="text-[10px] italic text-zinc-400">
                                          Kimse yok
                                        </span>
                                      )}
                                      <TooltipProvider delayDuration={120}>
                                        {assignees.map((u) => (
                                          <Tooltip key={u.id}>
                                            <TooltipTrigger asChild>
                                              <span
                                                data-testid={`milestone-assignee-${m.key}-${u.id}`}
                                                className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-violet-600 text-[8px] font-semibold text-white ring-2 ring-white cursor-default"
                                              >
                                                {nameInitials(u.name)}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent
                                              side="top"
                                              className="max-w-[220px] text-xs"
                                            >
                                              {u.name || "—"}
                                            </TooltipContent>
                                          </Tooltip>
                                        ))}
                                      </TooltipProvider>
                                    </div>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <button
                                          type="button"
                                          data-testid={`milestone-assignee-add-${m.key}`}
                                          className="ml-1 inline-flex h-4 items-center gap-0.5 rounded-full border border-dashed border-zinc-300 bg-white px-1.5 text-[9px] font-medium text-zinc-500 hover:border-violet-400 hover:text-violet-700"
                                        >
                                          <UserIcon className="h-2 w-2" />+ Ekle
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="start" className="w-56">
                                        <DropdownMenuLabel className="text-[11px]">
                                          Kişi ata — {m.label}
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        {allUsers.length === 0 ? (
                                          <div className="px-2 py-4 text-center text-[11px] text-zinc-400">
                                            Kullanıcı bulunamadı
                                          </div>
                                        ) : (
                                          allUsers.map((u) => (
                                            <DropdownMenuCheckboxItem
                                              key={u.id}
                                              checked={(m.assigned_user_ids || []).includes(u.id)}
                                              onCheckedChange={() =>
                                                toggleMilestoneAssignee(idx, u.id)
                                              }
                                              data-testid={`milestone-${m.key}-user-${u.id}`}
                                            >
                                              {u.name}
                                            </DropdownMenuCheckboxItem>
                                          ))
                                        )}
                                      </DropdownMenuContent>
                                    </DropdownMenu>

                                    {/* Deadline picker — inline calendar
                                        pop-out. Any tenant user may set /
                                        clear a deadline per user's spec. */}
                                    <div className="ml-2 flex items-center gap-1">
                                      <span className="text-[10px] text-zinc-500">Deadline:</span>
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <button
                                            type="button"
                                            data-testid={`milestone-deadline-trigger-${m.key}`}
                                            className={`inline-flex h-5 items-center gap-1 rounded-full border px-2 text-[10px] font-medium transition-colors ${
                                              m.deadline
                                                ? "border-violet-300 bg-violet-50 text-violet-700 hover:border-violet-400"
                                                : "border-dashed border-zinc-300 bg-white text-zinc-500 hover:border-violet-400 hover:text-violet-700"
                                            }`}
                                          >
                                            <Calendar className="h-2.5 w-2.5" />
                                            {m.deadline
                                              ? new Date(`${m.deadline}T00:00:00`).toLocaleDateString("tr-TR", {
                                                  day: "2-digit",
                                                  month: "short",
                                                  year: "numeric",
                                                })
                                              : "+ Tarih"}
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                          align="start"
                                          className="w-auto p-0"
                                          data-testid={`milestone-deadline-popover-${m.key}`}
                                        >
                                          <CalendarPicker
                                            mode="single"
                                            selected={
                                              m.deadline
                                                ? new Date(`${m.deadline}T00:00:00`)
                                                : undefined
                                            }
                                            onSelect={(d) => {
                                              if (!d) {
                                                setMilestoneDeadline(idx, null);
                                                return;
                                              }
                                              // yyyy-mm-dd in local time, matches
                                              // what the picker visually shows.
                                              const y = d.getFullYear();
                                              const mo = String(d.getMonth() + 1).padStart(2, "0");
                                              const da = String(d.getDate()).padStart(2, "0");
                                              setMilestoneDeadline(idx, `${y}-${mo}-${da}`);
                                            }}
                                            initialFocus
                                          />
                                          {m.deadline && (
                                            <div className="border-t border-zinc-100 p-2">
                                              <button
                                                type="button"
                                                onClick={() => setMilestoneDeadline(idx, null)}
                                                data-testid={`milestone-deadline-clear-${m.key}`}
                                                className="w-full rounded-md px-2 py-1 text-[11px] font-medium text-rose-600 hover:bg-rose-50"
                                              >
                                                Deadline&apos;ı kaldır
                                              </button>
                                            </div>
                                          )}
                                        </PopoverContent>
                                      </Popover>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {/* Add-subitem / add-group moved to Ayarlar
                              → "Kilometre Taşı Şablonu". */}
                        </div>
                      )}
                    </li>
                  );
                });
              })()}
            </ol>

            {isSuper && (
              <a
                href="/app/settings"
                data-testid="milestone-settings-link"
                className="mt-4 inline-flex items-center gap-2 rounded-lg border border-dashed border-violet-300 bg-violet-50/50 px-4 py-2 text-[13px] font-medium text-violet-700 transition-colors hover:bg-violet-100"
              >
                <Plus className="h-4 w-4" />
                Ayarlar&apos;dan grup / kategori düzenle
              </a>
            )}
          </div>
        </TabsContent>

        {/* === Notes === */}
        <TabsContent value="notes" className="mt-4">
          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            <Label className="mb-2 block">
              Takip Notları
              {(noteMentionIds.length > 0 || noteMilestoneKeys.length > 0) && (
                <span className="ml-2 inline-flex items-center gap-1.5 text-[11px]">
                  {noteMentionIds.length > 0 && (
                    <span className="rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700">
                      {noteMentionIds.length} @kişi
                    </span>
                  )}
                  {noteMilestoneKeys.length > 0 && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700">
                      {noteMilestoneKeys.length} #etiket
                    </span>
                  )}
                  <span className="text-zinc-500">— kaydedince bildirim gönderilir</span>
                </span>
              )}
            </Label>
            <MentionTextarea
              value={trackingNotes}
              onChange={setTrackingNotes}
              onMentionsChange={setNoteMentionIds}
              onTagsChange={setNoteMilestoneKeys}
              users={allUsers}
              tags={milestoneTagOptions}
              currentUserId={currentUser?.id}
              placeholder="Saha ziyareti, üretici teslim tarihi… @Ali Çoban veya #Kurulum ile etiketleyebilirsin."
              rows={12}
              data-testid="tracking-notes-textarea"
            />
            <p className="mt-2 text-[11px] text-zinc-500">
              @ ile kişiyi, # ile kilometre taşını etiketle. Üstteki
              &quot;Takibi kaydet&quot; butonuna basınca ilgili herkes
              bildirim alır.
            </p>
          </div>
        </TabsContent>

        {/* === Planned Activities (CRM-style) === */}
        <TabsContent value="planned" className="mt-4">
          <PlannedActivitiesTab
            constructionId={id}
            contacts={allContacts}
            companies={allCompanies}
            defaultCompanyId={row?.company_id}
            users={allUsers}
            currentUserId={currentUser?.id}
            companyName={row?.company_name}
          />
        </TabsContent>

        {/* === Activity (timeline) === */}
        <TabsContent value="activity" className="mt-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Left: comment composer (spans 1 col on md) */}
            <section className="rounded-xl border border-zinc-200 bg-white p-5">
              <h3 className="mb-2 text-sm font-semibold text-zinc-500">
                Yorum ekle
              </h3>
              <MentionTextarea
                value={comment}
                onChange={setComment}
                onMentionsChange={setMentionIds}
                onTagsChange={setCommentMilestoneKeys}
                users={allUsers}
                tags={milestoneTagOptions}
                currentUserId={currentUser?.id}
                placeholder="Örn. Bugün üretici ile görüşüldü, @Ali Çoban malzemeleri kontrol edecek. #Sevkiyat aşaması gecikebilir."
                data-testid="activity-comment-input"
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-500">
                  {mentionIds.length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 font-medium text-violet-700">
                      {mentionIds.length} @kişi
                    </span>
                  )}
                  {commentMilestoneKeys.length > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-700">
                      {commentMilestoneKeys.length} #etiket
                    </span>
                  )}
                  {mentionIds.length === 0 && commentMilestoneKeys.length === 0 && (
                    <span>Etiketlemek için @ veya # ile başla</span>
                  )}
                </div>
                <Button
                  onClick={postComment}
                  disabled={posting || !comment.trim()}
                  size="sm"
                  data-testid="activity-comment-submit"
                >
                  {posting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="mr-2 h-4 w-4" />
                  )}
                  Gönder
                </Button>
              </div>
              <p className="mt-3 text-[11px] text-zinc-500">
                Yorumlar zaman çizelgesine düşer; kilometre taşı geçişleri,
                dosya yüklemeleri ve statü değişimleri otomatik loglanır.
              </p>
            </section>

            {/* Right: timeline feed */}
            <section className="rounded-xl border border-zinc-200 bg-white p-5 md:col-span-2">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-sm font-semibold text-zinc-500">
                  Zaman Çizelgesi ({filteredActivities.length}
                  {timelineFilter === "mine" && ` / ${activities.length}`})
                </h3>
                <div
                  className="inline-flex rounded-full border border-zinc-200 bg-zinc-50 p-0.5 text-[11px]"
                  data-testid="timeline-filter"
                >
                  <button
                    type="button"
                    onClick={() => setTimelineFilter("all")}
                    data-testid="timeline-filter-all"
                    className={`rounded-full px-3 py-1 transition-colors ${
                      timelineFilter === "all"
                        ? "bg-white font-semibold text-zinc-900 shadow-sm"
                        : "text-zinc-500 hover:text-zinc-800"
                    }`}
                  >
                    Tümü
                  </button>
                  <button
                    type="button"
                    onClick={() => setTimelineFilter("mine")}
                    data-testid="timeline-filter-mine"
                    className={`rounded-full px-3 py-1 transition-colors ${
                      timelineFilter === "mine"
                        ? "bg-white font-semibold text-violet-800 shadow-sm"
                        : "text-zinc-500 hover:text-zinc-800"
                    }`}
                  >
                    Bana yönelik
                  </button>
                </div>
              </div>
              {filteredActivities.length === 0 ? (
                <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-8 text-center text-sm text-zinc-500">
                  {timelineFilter === "mine"
                    ? "Sana yönelik henüz aktivite yok. @kişi veya #etiket ile pinglendiğinde burada listelenir."
                    : "Henüz aktivite yok. Bir kilometre taşını işaretle veya yorum ekle."}
                </div>
              ) : (
                <ol className="relative space-y-4 border-l-2 border-zinc-100 pl-4">
                  {filteredActivities.map((a) => (
                    <ActivityRow
                      key={a.id}
                      activity={a}
                      mentionNames={allUsers.map((u) => u.name).filter(Boolean)}
                      tagLabels={milestones.map((m) => m.label).filter(Boolean)}
                    />
                  ))}
                </ol>
              )}
            </section>
          </div>
        </TabsContent>

        {/* === Files === */}
        <TabsContent value="files" className="mt-4">
          <FilesPanel
            files={files}
            thumbUrls={thumbUrls}
            uploading={uploading}
            uploadCategory={uploadCategory}
            setUploadCategory={setUploadCategory}
            fileInputRef={fileInputRef}
            uploadFile={uploadFile}
            downloadFile={downloadFile}
            removeFile={removeFile}
            isSuper={isSuper}
            currentUserId={currentUser?.id}
            fileCategoryTab={fileCategoryTab}
            setFileCategoryTab={setFileCategoryTab}
            isDragOver={isDragOver}
            setIsDragOver={setIsDragOver}
            openPreview={setPreviewFile}
            openShare={setShareFile}
            selectedFileIds={selectedFileIds}
            toggleFileSelected={toggleFileSelected}
            clearFileSelection={clearFileSelection}
            selectAllShownFiles={selectAllShownFiles}
            bulkDownloadFiles={bulkDownloadFiles}
            bulkDownloading={bulkDownloading}
          />
        </TabsContent>
      </Tabs>

      {/* Fullscreen preview modal — rendered outside the Tabs so it
          survives tab switches while the URL is still fetching. */}
      <FilePreviewModal
        file={previewFile}
        thumbUrl={previewFile ? thumbUrls[previewFile.id] : null}
        onClose={() => setPreviewFile(null)}
        onDownload={(f) => downloadFile(f.id, f.original_filename)}
      />

      {/* Public share link management — matches the cross-project
          browser at /app/cop/files. */}
      <ShareLinkDialog
        file={shareFile}
        onClose={() => setShareFile(null)}
      />
    </div>
  );
}

// Simple label/value row for the info panels above.
function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <dt className="shrink-0 text-xs text-zinc-500">{k}</dt>
      <dd className="text-right text-sm font-medium text-zinc-900">
        {v ? v : <span className="italic text-zinc-400">—</span>}
      </dd>
    </div>
  );
}

// Icon + colour palette per activity action. Keeps the timeline readable
// at a glance without needing to parse the meta blob.
const ACTIVITY_META = {
  created:               { icon: HardHat,       tint: "bg-zinc-100 text-zinc-700",         label: "Kayıt oluşturuldu" },
  comment_added:         { icon: MessageCircle, tint: "bg-violet-100 text-violet-700",     label: "Yorum" },
  note_mentioned:        { icon: StickyNote,    tint: "bg-fuchsia-100 text-fuchsia-700",   label: "Notta etiketleme" },
  milestone_completed:   { icon: CheckCircle2,  tint: "bg-emerald-100 text-emerald-700",   label: "Kilometre taşı tamamlandı" },
  milestone_uncompleted: { icon: Circle,        tint: "bg-amber-100 text-amber-700",       label: "Kilometre taşı geri alındı" },
  status_changed:        { icon: Activity,      tint: "bg-sky-100 text-sky-700",           label: "Durum güncellendi" },
  stage_changed:         { icon: Activity,      tint: "bg-sky-100 text-sky-700",           label: "Aşama güncellendi" },
  file_uploaded:         { icon: Upload,        tint: "bg-indigo-100 text-indigo-700",     label: "Dosya yüklendi" },
  file_deleted:          { icon: Trash2,        tint: "bg-rose-100 text-rose-700",         label: "Dosya silindi" },
};

function formatRelative(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

function ActivityRow({ activity, mentionNames = [], tagLabels = [] }) {
  const meta = ACTIVITY_META[activity.action] || ACTIVITY_META.created;
  const Icon = meta.icon;
  let title = meta.label;
  let detail = null;
  if (activity.action === "milestone_completed" || activity.action === "milestone_uncompleted") {
    detail = <span className="font-semibold">{activity.meta?.label || activity.meta?.key}</span>;
  } else if (activity.action === "status_changed") {
    detail = <span>{activity.meta?.from} → <strong>{activity.meta?.to}</strong></span>;
  } else if (activity.action === "stage_changed") {
    detail = <span>{activity.meta?.from || "—"} → <strong>{activity.meta?.to}</strong></span>;
  } else if (activity.action === "file_uploaded" || activity.action === "file_deleted") {
    detail = <span className="font-medium">{activity.meta?.filename}</span>;
  }
  const mentionCount = (activity.meta?.mentioned_user_ids || []).length;
  const tagCount = (activity.meta?.mentioned_milestone_keys || []).length;
  return (
    <li className="relative">
      <span
        className={`absolute -left-[26px] flex h-6 w-6 items-center justify-center rounded-full ring-2 ring-white ${meta.tint}`}
      >
        <Icon className="h-3 w-3" />
      </span>
      <div className="rounded-lg border border-zinc-100 bg-white p-3 shadow-sm">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-zinc-900">{activity.actor_name || "—"}</span>
          <span className="shrink-0 text-[11px] text-zinc-400">{formatRelative(activity.created_at)}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-zinc-600">
          <span>{title}{detail && <> · {detail}</>}</span>
          {mentionCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700">
              {mentionCount} @kişi
            </span>
          )}
          {tagCount > 0 && (
            <span className="inline-flex items-center rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
              {tagCount} #etiket
            </span>
          )}
        </div>
        {activity.text && (
          <p className="mt-2 whitespace-pre-wrap rounded-md bg-zinc-50 p-2 text-sm text-zinc-800">
            <MentionRichText text={activity.text} mentionNames={mentionNames} tagLabels={tagLabels} />
          </p>
        )}
      </div>
    </li>
  );
}

function humanSize(bytes) {
  return humanFileSize(bytes);
}

// Left-rail counts, keyed by bucket. `all` = total.
function useCategoryCounts(files) {
  return useMemo(() => {
    const counts = { all: files.length };
    for (const c of FILE_CATEGORIES) counts[c.key] = 0;
    for (const f of files) counts[bucketOf(f.category)] += 1;
    return counts;
  }, [files]);
}

function FilesPanel({
  files,
  thumbUrls,
  uploading,
  uploadCategory,
  setUploadCategory,
  fileInputRef,
  uploadFile,
  downloadFile,
  removeFile,
  isSuper,
  currentUserId,
  fileCategoryTab,
  setFileCategoryTab,
  isDragOver,
  setIsDragOver,
  openPreview,
  openShare,
  selectedFileIds,
  toggleFileSelected,
  clearFileSelection,
  selectAllShownFiles,
  bulkDownloadFiles,
  bulkDownloading,
}) {
  const counts = useCategoryCounts(files);
  const shown = useMemo(() => {
    if (fileCategoryTab === "all") return files;
    return files.filter((f) => bucketOf(f.category) === fileCategoryTab);
  }, [files, fileCategoryTab]);
  const isAll = fileCategoryTab === "all";
  const activeCat = !isAll ? CATEGORY_META[fileCategoryTab] : null;

  // On category-specific tabs the upload always lands in that bucket.
  // The bound `uploadCategory` state stays in sync so the header button
  // sends the right FormData without another picker.
  useEffect(() => {
    if (!isAll) setUploadCategory(fileCategoryTab);
  }, [fileCategoryTab, isAll, setUploadCategory]);

  const dropHandlers = {
    onDragOver: (e) => {
      e.preventDefault();
      setIsDragOver(true);
    },
    onDragLeave: () => setIsDragOver(false),
    onDrop: (e) => {
      e.preventDefault();
      setIsDragOver(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) uploadFile(f);
    },
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[220px_1fr]">
      {/* --- Left vertical rail --- */}
      <aside
        className="flex flex-col gap-1 rounded-xl border border-zinc-200 bg-white p-2"
        data-testid="file-category-rail"
      >
        <RailButton
          active={isAll}
          onClick={() => setFileCategoryTab("all")}
          Icon={Folder}
          label="Tümü"
          count={counts.all}
          testid="file-cat-all"
          tint="bg-zinc-100 text-zinc-800"
        />
        <div className="my-1 h-px bg-zinc-100" />
        {FILE_CATEGORIES.map((c) => (
          <RailButton
            key={c.key}
            active={fileCategoryTab === c.key}
            onClick={() => setFileCategoryTab(c.key)}
            Icon={c.Icon}
            label={c.label}
            count={counts[c.key] || 0}
            testid={`file-cat-${c.key}`}
            tint={c.tint}
          />
        ))}
      </aside>

      {/* --- Right main content --- */}
      <section
        {...dropHandlers}
        className="relative min-w-0 rounded-xl border border-zinc-200 bg-white p-5"
      >
        {/* Header: category label + count on the left, "Dosya ekle"
            action on the right. Same shape on every tab so the button
            never jumps around. */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {activeCat ? (
              <span className={`flex h-8 w-8 items-center justify-center rounded-md ${activeCat.tint}`}>
                <activeCat.Icon className="h-4 w-4" />
              </span>
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-100 text-zinc-700">
                <Folder className="h-4 w-4" />
              </span>
            )}
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold text-zinc-900">
                {isAll ? "Tüm dosyalar" : activeCat?.label}
              </h3>
              <div className="text-[11px] text-zinc-500">
                {shown.length} dosya · max 500 MB
              </div>
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* Only on "Tümü" the user must pick a category BEFORE
                uploading (there's no active bucket to inherit from). */}
            {isAll && (
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger
                  data-testid="file-category-select"
                  className="h-9 w-[160px] bg-white text-[13px]"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FILE_CATEGORIES.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <input
              ref={fileInputRef}
              type="file"
              onChange={(e) => uploadFile(e.target.files?.[0])}
              className="hidden"
              data-testid="file-upload-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="file-upload-btn"
              size="sm"
            >
              {uploading ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-1.5 h-4 w-4" />
              )}
              Dosya ekle
            </Button>
          </div>
        </div>

        {/* Selection toolbar — mirrors the cross-project browser so
            both surfaces feel identical. Appears when 1+ files are
            selected. Positioned right below the header for consistency. */}
        {selectedFileIds && selectedFileIds.size > 0 && (
          <div
            className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2"
            data-testid="file-selection-toolbar"
          >
            <span
              className="inline-flex items-center gap-1 rounded-full bg-violet-600 px-2.5 py-1 text-[11px] font-semibold text-white"
              data-testid="file-selection-count"
            >
              <CheckCheck className="h-3 w-3" />
              {selectedFileIds.size} dosya seçildi
            </span>
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={clearFileSelection}
                data-testid="file-selection-clear"
              >
                <X className="mr-1.5 h-3.5 w-3.5" />
                Temizle
              </Button>
              <Button
                onClick={bulkDownloadFiles}
                disabled={bulkDownloading}
                size="sm"
                data-testid="file-bulk-download"
                className="bg-violet-600 text-white hover:bg-violet-700"
              >
                {bulkDownloading ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Archive className="mr-1.5 h-3.5 w-3.5" />
                )}
                ZIP olarak indir
              </Button>
            </div>
          </div>
        )}

        {/* Files grid */}
        {shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50 p-10 text-center text-sm text-zinc-500">
            {files.length === 0
              ? "Henüz dosya yok. Sağ üstteki “Dosya ekle” ile başla ya da bu alana dosya sürükle."
              : isAll
                ? "Henüz dosya yok."
                : `${activeCat?.label} kategorisinde dosya yok. Sağ üstteki “Dosya ekle” ile ekle ya da bu alana sürükle.`}
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center justify-end">
              <button
                type="button"
                onClick={() => selectAllShownFiles(shown)}
                data-testid="file-select-all"
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
              >
                {shown.every((f) => selectedFileIds?.has(f.id)) ? (
                  <><CheckSquare className="h-3.5 w-3.5 text-violet-600" /> Tümünün seçimini kaldır</>
                ) : (
                  <><Square className="h-3.5 w-3.5" /> Tümünü seç</>
                )}
              </button>
            </div>
            <ul
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
              data-testid="files-list"
            >
              {shown.map((f) => (
                <FileCard
                  key={f.id}
                  file={f}
                  thumbUrl={thumbUrls[f.id] || null}
                  selected={selectedFileIds?.has(f.id) || false}
                  onToggleSelect={() => toggleFileSelected(f.id)}
                  onDownload={() => downloadFile(f.id, f.original_filename)}
                  onPreview={() => openPreview(f)}
                  onShare={() => openShare(f)}
                  onDelete={
                    isSuper || currentUserId === f.uploaded_by
                      ? () => removeFile(f.id, f.original_filename)
                      : null
                  }
                />
              ))}
            </ul>
          </>
        )}

        {/* Drag-drop overlay — only shown while a file is being dragged
            over the panel. Keeps the resting UI clean but still exposes
            the drop target when the user needs it. */}
        {isDragOver && (
          <div
            data-testid="file-dropzone-overlay"
            className="pointer-events-none absolute inset-2 z-10 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-violet-400 bg-violet-50/90 backdrop-blur-sm"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-md">
              <Upload className="h-6 w-6 text-violet-600" />
            </div>
            <div className="text-sm font-semibold text-violet-800">
              Dosyayı bırak
            </div>
            <div className="text-[11px] text-violet-700">
              {isAll ? "Kategori sağdaki menüden seçili" : `Kategori: ${activeCat?.label}`}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function RailButton({ active, onClick, Icon, label, count, testid, tint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors ${
        active
          ? "bg-zinc-900 text-white"
          : "text-zinc-700 hover:bg-zinc-50"
      }`}
    >
      <span
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
          active ? "bg-white/15 text-white" : tint
        }`}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="flex-1 truncate">{label}</span>
      <span
        className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${
          active ? "bg-white/15 text-white" : "bg-zinc-100 text-zinc-600"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function FilePreviewModal({ file, thumbUrl, onClose, onDownload }) {
  // For images we already have a blob URL from the grid cache; for PDFs
  // we fetch it lazily when the modal opens. Non-image / non-pdf types
  // just show a placeholder + download button.
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const isImage = file && (file.content_type || "").startsWith("image/");
  const isPdf = file && file.content_type === "application/pdf";

  useEffect(() => {
    let cancelled = false;
    if (!file || !isPdf) {
      setPdfUrl(null);
      return () => {};
    }
    setPdfLoading(true);
    (async () => {
      try {
        const res = await api.get(`/construction-files/${file.id}/download`, {
          responseType: "blob",
        });
        if (cancelled) return;
        setPdfUrl(URL.createObjectURL(res.data));
      } catch {
        toast.error("Önizleme yüklenemedi");
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      // Revoke inline so we don't leak on every open/close cycle.
      setPdfUrl((prev) => {
        if (prev) try { URL.revokeObjectURL(prev); } catch { /* noop */ }
        return null;
      });
    };
  }, [file, isPdf]);

  const cat = file ? CATEGORY_META[bucketOf(file.category)] || CATEGORY_META.other : null;

  return (
    <Dialog open={!!file} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        data-testid="file-preview-modal"
        className="flex h-[85vh] max-w-6xl flex-col gap-0 overflow-hidden p-0"
      >
        {file && (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-zinc-200 bg-white px-4 py-3">
              <span className={`flex h-8 w-8 items-center justify-center rounded-md ${cat.tint}`}>
                <cat.Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <DialogTitle className="truncate text-sm font-semibold text-zinc-900">
                  {file.original_filename}
                </DialogTitle>
                <div className="text-[11px] text-zinc-500">
                  {cat.label} · {humanSize(file.size)} · {file.uploaded_by_name}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownload(file)}
                data-testid="file-preview-download"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> İndir
              </Button>
              <button
                type="button"
                onClick={onClose}
                data-testid="file-preview-close"
                className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                aria-label="Kapat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex flex-1 items-center justify-center overflow-auto bg-zinc-950 p-4">
              {isImage && thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt={file.original_filename}
                  data-testid="file-preview-image"
                  className="max-h-full max-w-full object-contain shadow-2xl"
                />
              ) : isPdf ? (
                pdfLoading || !pdfUrl ? (
                  <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
                ) : (
                  <iframe
                    src={pdfUrl}
                    title={file.original_filename}
                    data-testid="file-preview-pdf"
                    className="h-full w-full rounded-md bg-white"
                  />
                )
              ) : (
                <div className="flex flex-col items-center gap-3 text-center text-zinc-300">
                  <div className={`flex h-20 w-20 items-center justify-center rounded-2xl ${cat.tint}`}>
                    <cat.Icon className="h-10 w-10" />
                  </div>
                  <div className="text-sm font-medium text-white">
                    Bu dosya türü tarayıcıda önizlenemiyor.
                  </div>
                  <div className="text-[12px] text-zinc-400">
                    İndir düğmesini kullanarak inceleyebilirsin.
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FileCard({ file, onDownload, onDelete, onPreview, onShare, thumbUrl, selected, onToggleSelect }) {
  const cat = CATEGORY_META[bucketOf(file.category)] || CATEGORY_META.other;
  const Icon = cat.Icon;
  const isImage = (file.content_type || "").startsWith("image/");
  const isPdf = file.content_type === "application/pdf";
  return (
    <li
      data-testid={`file-card-${file.id}`}
      data-selected={selected ? "true" : "false"}
      className={`group relative flex flex-col overflow-hidden rounded-xl border bg-white transition-all hover:-translate-y-0.5 hover:shadow-md ${
        selected
          ? "border-violet-400 ring-2 ring-violet-200"
          : "border-zinc-200 hover:border-violet-300"
      }`}
    >
      {/* Selection checkbox — visible on hover / always when selected. */}
      {onToggleSelect && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(); }}
          data-testid={`file-select-${file.id}`}
          aria-label={selected ? "Seçimi kaldır" : "Dosyayı seç"}
          className={`absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-md border transition-all ${
            selected
              ? "border-violet-500 bg-violet-600 text-white opacity-100 shadow"
              : "border-zinc-300 bg-white/90 text-zinc-500 opacity-0 backdrop-blur group-hover:opacity-100 hover:border-violet-400 hover:text-violet-600"
          }`}
        >
          {selected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
        </button>
      )}

      {/* Category strip */}
      <div className={`flex items-center gap-2 px-3 py-1.5 ${onToggleSelect ? "pl-10" : ""} text-[10px] font-semibold ${cat.tint}`}>
        <Icon className="h-3 w-3" />
        <span className="uppercase tracking-wide">{cat.label}</span>
        <span className="ml-auto text-[10px] font-normal opacity-70">{humanSize(file.size)}</span>
      </div>

      {/* Preview area — image thumb OR icon placeholder. Clickable when
          previewable so the whole big canvas doubles as the trigger. */}
      <button
        type="button"
        onClick={onPreview}
        data-testid={`file-preview-trigger-${file.id}`}
        disabled={!isImage && !isPdf}
        className="relative flex h-40 w-full items-center justify-center overflow-hidden bg-zinc-50 disabled:cursor-default"
      >
        {isImage && thumbUrl ? (
          <img
            src={thumbUrl}
            alt={file.original_filename}
            className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]"
            data-testid={`file-thumb-${file.id}`}
          />
        ) : isImage ? (
          // Blob still loading
          <Loader2 className="h-6 w-6 animate-spin text-zinc-300" />
        ) : (
          <div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${cat.tint}`}>
            {isPdf ? <FileText className="h-8 w-8" /> : <Icon className="h-8 w-8" />}
          </div>
        )}
        {(isImage || isPdf) && (
          <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
            <Eye className="h-3 w-3" /> Önizle
          </span>
        )}
      </button>

      {/* Meta strip */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-zinc-900" title={file.original_filename}>
            {file.original_filename}
          </div>
          <div className="mt-0.5 text-[11px] text-zinc-500">
            {file.uploaded_by_name} · {formatRelative(file.created_at)}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-auto flex border-t border-zinc-100">
        <button
          type="button"
          onClick={onDownload}
          data-testid={`file-download-${file.id}`}
          className="flex flex-1 items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
        >
          <Download className="h-3.5 w-3.5" /> İndir
        </button>
        {onShare && (
          <>
            <div className="w-px bg-zinc-100" />
            <button
              type="button"
              onClick={onShare}
              data-testid={`file-share-${file.id}`}
              className="flex flex-1 items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-violet-600 transition-colors hover:bg-violet-50"
            >
              <Share2 className="h-3.5 w-3.5" /> Paylaş
            </button>
          </>
        )}
        {onDelete && (
          <>
            <div className="w-px bg-zinc-100" />
            <button
              type="button"
              onClick={onDelete}
              data-testid={`file-delete-${file.id}`}
              className="flex flex-1 items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium text-rose-600 transition-colors hover:bg-rose-50"
            >
              <Trash2 className="h-3.5 w-3.5" /> Sil
            </button>
          </>
        )}
      </div>
    </li>
  );
}
